/**
 * Tâche 1424 (Eric 2026-10-01) — Fenêtre de création d'un sondage.
 *
 * Volontairement simple, comme Eric l'a demandé : UNE question, plusieurs
 * réponses, et trois interrupteurs. Deux champs de réponse sont proposés au
 * départ — en offrir un seul laisserait croire qu'un sondage à une réponse
 * est possible, en offrir cinq donnerait un formulaire à remplir.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plus, Trash, ChartBar } from "@phosphor-icons/react";

import { API_BASE as API } from "../../lib/apiBase";
import { useLang } from "../../contexts/LanguageContext";

const MAX_REPONSES = 12;

export default function PollComposer({ channelId, conversationId, onClose, onCreated }) {
  const { tr } = useLang();
  const [question, setQuestion] = useState("");
  const [reponses, setReponses] = useState(["", ""]);
  const [multiple, setMultiple] = useState(false);
  // Tâche 1450 (Eric 2026-06) — « on » par défaut, sur sa demande : un sondage
  // sert d'abord à recueillir ce qu'on n'avait pas prévu. Les sondages créés
  // AVANT cette tâche gardent leur réglage : la bascule se fait sondage par
  // sondage depuis la carte, jamais en masse.
  const [ajoutLibre, setAjoutLibre] = useState(true);
  const [anonyme, setAnonyme] = useState(false);
  // Tâche 1426 — Date de clôture OPTIONNELLE : sans elle, « prévenir avant la
  // clôture » n'a aucun « avant ». Laissée vide, le sondage reste ouvert
  // jusqu'à ce qu'on le clôt à la main, ce qui est le cas courant.
  const [cloture, setCloture] = useState("");
  const [envoi, setEnvoi] = useState(false);

  const remplies = reponses.map((r) => r.trim()).filter(Boolean);
  const pret = question.trim().length > 0 && new Set(remplies.map((r) => r.toLowerCase())).size >= 2;

  const modifier = (i, valeur) => {
    setReponses((prec) => prec.map((r, idx) => (idx === i ? valeur : r)));
  };

  const creer = async () => {
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API}/api/polls`, {
        question: question.trim(),
        options: reponses,
        channel_id: channelId || null,
        conversation_id: conversationId || null,
        multiple,
        allow_add_options: ajoutLibre,
        anonymous: anonyme,
        closes_at: cloture ? new Date(cloture).toISOString() : null,
      }, { withCredentials: true });
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail
        || tr("Création du sondage impossible.", "Could not create the poll."));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="poll-composer-overlay"
    >
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <h3 className="flex items-center gap-2 text-base font-bold text-zinc-900">
            <ChartBar size={18} weight="bold" className="text-indigo-700" />
            {tr("Nouveau sondage", "New poll")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-800"
            data-testid="poll-composer-close"
            aria-label={tr("Fermer", "Close")}
          >
            <X size={18} />
          </button>
        </div>

        <label className="mt-4 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
          {tr("Question", "Question")}
        </label>
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          maxLength={300}
          placeholder={tr("Ex. : on se voit quel jour ?", "e.g. which day works for you?")}
          className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40"
          data-testid="poll-composer-question"
          autoFocus
        />

        <label className="mt-4 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
          {tr("Réponses proposées", "Answers")}
        </label>
        <div className="mt-1 space-y-1.5">
          {reponses.map((valeur, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input
                type="text"
                value={valeur}
                onChange={(e) => modifier(i, e.target.value)}
                maxLength={120}
                placeholder={tr(`Réponse ${i + 1}`, `Answer ${i + 1}`)}
                className="flex-1 rounded-lg border border-zinc-300 px-3 py-1.5 text-sm"
                data-testid={`poll-composer-option-${i}`}
              />
              {/* Le retrait n'est offert qu'au-delà de deux champs : supprimer
                  le deuxième donnerait un sondage à une seule réponse, que le
                  serveur refuse — mieux vaut ne pas le proposer du tout. */}
              {reponses.length > 2 && (
                <button
                  type="button"
                  onClick={() => setReponses((p) => p.filter((_, idx) => idx !== i))}
                  className="p-1.5 text-zinc-400 hover:text-rose-600"
                  data-testid={`poll-composer-remove-${i}`}
                  aria-label={tr("Retirer cette réponse", "Remove this answer")}
                >
                  <Trash size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
        {reponses.length < MAX_REPONSES && (
          <button
            type="button"
            onClick={() => setReponses((p) => [...p, ""])}
            className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-semibold text-indigo-700 hover:underline"
            data-testid="poll-composer-add-option"
          >
            <Plus size={12} weight="bold" />
            {tr("Ajouter une réponse", "Add an answer")}
          </button>
        )}

        <div className="mt-4 border-t border-zinc-100 pt-3">
          <label
            className="block text-[11px] font-semibold uppercase tracking-wide text-zinc-500"
            htmlFor="poll-closes-at"
          >
            {tr("Clôture (facultatif)", "Closing date (optional)")}
          </label>
          <input
            id="poll-closes-at"
            type="datetime-local"
            value={cloture}
            onChange={(e) => setCloture(e.target.value)}
            className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-1.5 text-sm"
            data-testid="poll-composer-closes-at"
          />
          <p className="mt-1 text-[11px] leading-snug text-zinc-500">
            {cloture
              ? tr("Ceux qui n'ont pas répondu recevront un rappel 24 h avant.",
                   "Those who have not answered will get a reminder 24 h before.")
              : tr("Sans date, le sondage reste ouvert jusqu'à ce que tu le closes.",
                   "With no date, the poll stays open until you close it.")}
          </p>
        </div>

        <div className="mt-4 space-y-2 border-t border-zinc-100 pt-3">
          <Interrupteur
            testid="poll-composer-multiple"
            coche={multiple}
            onChange={setMultiple}
            titre={tr("Plusieurs réponses permises", "Allow multiple answers")}
            aide={tr(
              "Chaque personne peut cocher plus d'une réponse.",
              "Each person can tick more than one answer.",
            )}
          />
          <Interrupteur
            testid="poll-composer-allow-add"
            coche={ajoutLibre}
            onChange={setAjoutLibre}
            titre={tr("Les autres peuvent ajouter des réponses", "Others can add answers")}
            aide={tr(
              "La réponse ajoutée portera le nom de qui l'a proposée.",
              "An added answer shows who proposed it.",
            )}
          />
          <Interrupteur
            testid="poll-composer-anonymous"
            coche={anonyme}
            onChange={setAnonyme}
            titre={tr("Vote anonyme", "Anonymous vote")}
            aide={tr(
              "Seuls les totaux sont affichés. Les noms ne quittent jamais le serveur : ce choix ne pourra plus être changé après l'envoi.",
              "Only totals are shown. Names never leave the server: this choice cannot be changed after sending.",
            )}
          />
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-100"
            data-testid="poll-composer-cancel"
          >
            {tr("Annuler", "Cancel")}
          </button>
          <button
            type="button"
            onClick={creer}
            disabled={!pret || envoi}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-zinc-300"
            data-testid="poll-composer-submit"
          >
            {envoi ? tr("Envoi…", "Sending…") : tr("Publier le sondage", "Post poll")}
          </button>
        </div>
        {!pret && (
          <p className="mt-2 text-right text-[11px] text-zinc-500" data-testid="poll-composer-hint">
            {tr("Une question et deux réponses différentes au minimum.",
                "A question and at least two different answers.")}
          </p>
        )}
      </div>
    </div>
  );
}

function Interrupteur({ coche, onChange, titre, aide, testid }) {
  return (
    <label className="flex cursor-pointer items-start gap-2">
      <input
        type="checkbox"
        checked={coche}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-indigo-600"
        data-testid={testid}
      />
      <span className="min-w-0">
        <span className="block text-[13px] font-medium text-zinc-800">{titre}</span>
        <span className="block text-[11px] leading-snug text-zinc-500">{aide}</span>
      </span>
    </label>
  );
}
